import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Mail, KeyRound, CheckCircle, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') || 'login';
  const verifyToken = searchParams.get('token');
  
  const [mode, setMode] = useState(initialMode); // 'login', 'register', 'forgot', 'reset', 'verify-sent', 'verifying', 'verified'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  // Honeypot field – kept empty by humans, often filled by bots.
  const [website, setWebsite] = useState('');

  // Handle email verification on mount
  useEffect(() => {
    if (initialMode === 'verify' && verifyToken) {
      verifyEmail(verifyToken);
    }
  }, [initialMode, verifyToken]);

  const verifyEmail = async (token) => {
    setMode('verifying');
    try {
      const response = await axios.get(`${API}/auth/verify/${token}`);
      setVerifyMessage(response.data.message);
      setMode('verified');
      toast.success(response.data.message);
    } catch (error) {
      setVerifyMessage(error.response?.data?.detail || 'Verifizierung fehlgeschlagen');
      setMode('verified');
      toast.error(error.response?.data?.detail || 'Verifizierung fehlgeschlagen');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      toast.success('Willkommen zurück!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'E-Mail oder Passwort falsch';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 6) errors.push('mindestens 6 Zeichen');
    if (!/[A-Z]/.test(pwd)) errors.push('ein Großbuchstabe');
    if (!/[0-9]/.test(pwd)) errors.push('eine Zahl');
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(pwd)) errors.push('ein Sonderzeichen');
    return errors;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Passwort-Validierung
    if (password !== confirmPassword) {
      toast.error('Die Passwörter stimmen nicht überein');
      return;
    }
    
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      toast.error(`Passwort benötigt: ${passwordErrors.join(', ')}`);
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, website });
      
      // Show verification screen - user must verify email before login
      setMode('verify-sent');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setMode('reset');
      toast.success('Einmalcode wurde an deine E-Mail gesendet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-Mail-Adresse nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      toast.error(`Passwort benötigt: ${passwordErrors.join(', ')}`);
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { 
        email, 
        code: resetCode, 
        newPassword 
      });
      toast.success('Passwort erfolgreich zurückgesetzt! Du kannst dich jetzt anmelden.');
      setMode('login');
      setPassword('');
      setResetCode('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ungültiger Code oder abgelaufen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            data-testid="back-to-home-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zur Startseite
          </button>
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="UniSide" className="w-20 h-20 object-contain" />
          </div>
          <h1 className="text-4xl font-bold font-outfit text-slate-900 mb-2">UniSide</h1>
          <p className="text-slate-600">Finde deinen perfekten Studentenjob.</p>
        </div>

        {/* Login Form */}
        {mode === 'login' && (
          <Card className="border-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-2xl">
            <CardHeader>
              <CardTitle className="font-outfit">Anmelden</CardTitle>
              <CardDescription>
                Melde dich mit deinem Konto an
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="deine@email.at"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password">Passwort</Label>
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      className="text-xs text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Passwort vergessen?
                    </button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 rounded-xl"
                    data-testid="password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-all"
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      Anmelden
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setMode('register')}
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="go-to-register"
                >
                  Noch kein Konto? <span className="text-primary font-medium">Registrieren</span>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Register Form */}
        {mode === 'register' && (
          <Card className="border-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-2xl">
            <CardHeader>
              <CardTitle className="font-outfit">Registrieren</CardTitle>
              <CardDescription>
                Erstelle ein neues Konto
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Honeypot – hidden via CSS + aria + autocomplete, real users never fill this */}
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '-10000px',
                    top: 'auto',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                  }}
                >
                  <label htmlFor="website">Website (bitte leer lassen)</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email">E-Mail</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="deine@email.at"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Passwort</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    placeholder="Mind. 6 Zeichen, Großbuchstabe, Zahl, Sonderzeichen"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-12 rounded-xl"
                    data-testid="register-password-input"
                  />
                  {password && (
                    <div className="text-xs space-y-1 mt-2">
                      <p className={password.length >= 6 ? 'text-green-600' : 'text-gray-400'}>
                        {password.length >= 6 ? '✓' : '○'} Mindestens 6 Zeichen
                      </p>
                      <p className={/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
                        {/[A-Z]/.test(password) ? '✓' : '○'} Ein Großbuchstabe
                      </p>
                      <p className={/[0-9]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
                        {/[0-9]/.test(password) ? '✓' : '○'} Eine Zahl
                      </p>
                      <p className={/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
                        {/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(password) ? '✓' : '○'} Ein Sonderzeichen (!@#$%...)
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Passwort wiederholen"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={`h-12 rounded-xl ${
                      confirmPassword && password !== confirmPassword 
                        ? 'border-red-500 focus:ring-red-500' 
                        : confirmPassword && password === confirmPassword
                        ? 'border-green-500 focus:ring-green-500'
                        : ''
                    }`}
                    data-testid="confirm-password-input"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500">Passwörter stimmen nicht überein</p>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <p className="text-xs text-green-500 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Passwörter stimmen überein
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-all"
                  disabled={loading || password !== confirmPassword}
                  data-testid="register-button"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      Registrieren
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-4 p-3 bg-amber-50 rounded-xl text-xs text-amber-800">
                <strong>Hinweis:</strong> Nach der Registrierung erhältst du eine E-Mail zur Bestätigung. 
                Unbestätigte Konten werden nach 7 Tagen automatisch gelöscht.
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setMode('login')}
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="go-to-login"
                >
                  Schon registriert? <span className="text-primary font-medium">Anmelden</span>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Verification Email Sent */}
        {mode === 'verify-sent' && (
          <Card className="border-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold font-outfit mb-2">Bestätige deine E-Mail</h2>
              <p className="text-slate-600 mb-6">
                Wir haben eine Bestätigungs-E-Mail an <strong>{email}</strong> gesendet. 
                Klicke auf den Link in der E-Mail, um dein Konto zu aktivieren.
              </p>
              <div className="p-4 bg-slate-50 rounded-xl text-sm text-slate-600 mb-6">
                <p><strong>Keine E-Mail erhalten?</strong></p>
                <p>Prüfe deinen Spam-Ordner oder warte einige Minuten.</p>
              </div>
              <Button
                onClick={() => setMode('login')}
                variant="outline"
                className="rounded-full"
                data-testid="back-to-login-from-verify"
              >
                Zurück zur Anmeldung
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Forgot Password Form */}
        {mode === 'forgot' && (
          <Card className="border-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-2xl">
            <CardHeader>
              <CardTitle className="font-outfit">Passwort vergessen?</CardTitle>
              <CardDescription>
                Gib deine E-Mail ein und wir senden dir einen Einmalcode
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">E-Mail</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="deine@email.at"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                  disabled={loading}
                  data-testid="send-reset-code-button"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Mail className="mr-2 w-4 h-4" />
                      Einmalcode senden
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setMode('login')}
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="back-to-login-from-forgot"
                >
                  <ArrowLeft className="inline w-4 h-4 mr-1" />
                  Zurück zur Anmeldung
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reset Password Form */}
        {mode === 'reset' && (
          <Card className="border-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-2xl">
            <CardHeader>
              <CardTitle className="font-outfit">Neues Passwort setzen</CardTitle>
              <CardDescription>
                Gib den Einmalcode aus der E-Mail und dein neues Passwort ein
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-code">Einmalcode (6 Ziffern)</Label>
                  <Input
                    id="reset-code"
                    type="text"
                    placeholder="123456"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    className="h-12 rounded-xl text-center text-2xl tracking-widest font-mono"
                    data-testid="reset-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Neues Passwort</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Mind. 6 Zeichen, Großbuchstabe, Zahl, Sonderzeichen"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-12 rounded-xl"
                    data-testid="new-password-input"
                  />
                  {newPassword && (
                    <div className="text-xs space-y-1 mt-2">
                      <p className={newPassword.length >= 6 ? 'text-green-600' : 'text-gray-400'}>
                        {newPassword.length >= 6 ? '✓' : '○'} Mindestens 6 Zeichen
                      </p>
                      <p className={/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}>
                        {/[A-Z]/.test(newPassword) ? '✓' : '○'} Ein Großbuchstabe
                      </p>
                      <p className={/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}>
                        {/[0-9]/.test(newPassword) ? '✓' : '○'} Eine Zahl
                      </p>
                      <p className={/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}>
                        {/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(newPassword) ? '✓' : '○'} Ein Sonderzeichen
                      </p>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                  disabled={loading || resetCode.length !== 6}
                  data-testid="reset-password-button"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <KeyRound className="mr-2 w-4 h-4" />
                      Passwort zurücksetzen
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-4 p-3 bg-blue-50 rounded-xl text-xs text-blue-800">
                Der Code ist 15 Minuten gültig.
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setMode('forgot')}
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="request-new-code"
                >
                  Neuen Code anfordern
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Verifying Email (Loading State) */}
        {mode === 'verifying' && (
          <Card className="border-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold font-outfit mb-2">E-Mail wird verifiziert...</h2>
              <p className="text-slate-600">Bitte warte einen Moment.</p>
            </CardContent>
          </Card>
        )}

        {/* Email Verified Result */}
        {mode === 'verified' && (
          <Card className="border-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                verifyMessage.includes('erfolgreich') ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {verifyMessage.includes('erfolgreich') || verifyMessage.includes('bereits') ? (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                ) : (
                  <Mail className="w-8 h-8 text-red-600" />
                )}
              </div>
              <h2 className="text-2xl font-bold font-outfit mb-2">
                {verifyMessage.includes('erfolgreich') || verifyMessage.includes('bereits') 
                  ? 'E-Mail bestätigt!' 
                  : 'Verifizierung fehlgeschlagen'}
              </h2>
              <p className="text-slate-600 mb-6">{verifyMessage}</p>
              <Button
                onClick={() => {
                  setMode('login');
                  navigate('/auth');
                }}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="go-to-login-after-verify"
              >
                Zur Anmeldung
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
